import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Tag, Package, Send, Trash2, Search, Filter, Loader2, ArrowRightLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// Searchable Dropdown (mirrors FG Assignment pattern)
const SearchableDropdown = ({
  label, placeholder, options, value, onChange, disabled, isLoading,
  displayKey = null, valueKey = null, testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDisplay = (opt) => (displayKey ? opt[displayKey] : opt);
  const getValue = (opt) => (valueKey ? opt[valueKey] : opt);

  const filtered = useMemo(() => {
    if (!searchTerm) return options;
    const t = searchTerm.toLowerCase();
    return options.filter(o => String(getDisplay(o) ?? '').toLowerCase().includes(t));
  }, [options, searchTerm, displayKey]);

  const selectedDisplay = useMemo(() => {
    if (!value) return '';
    const f = options.find(o => getValue(o) === value);
    return f ? getDisplay(f) : value;
  }, [value, options, displayKey, valueKey]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:border-gray-400'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          data-testid={testId}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />Loading...
            </div>
          ) : (
            <span className={selectedDisplay ? 'text-foreground' : 'text-muted-foreground'}>
              {selectedDisplay || placeholder}
            </span>
          )}
        </div>
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..." className="pl-8 h-9" autoFocus />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
              ) : (
                filtered.map((opt, i) => (
                  <div key={i}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${getValue(opt) === value ? 'bg-fuchsia-50 text-fuchsia-700' : ''}`}
                    onClick={() => { onChange(getValue(opt)); setIsOpen(false); setSearchTerm(''); }}
                  >{getDisplay(opt)}</div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DesignTransfer = () => {
  const navigate = useNavigate();

  const operator = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);

  // Destination FG cascade (matches Goods Transfer FG Assignment)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [designs, setDesigns] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [items, setItems] = useState([]);
  const [isCategoriesLoading, setCategoriesLoading] = useState(false);
  const [isDesignsLoading, setDesignsLoading] = useState(false);
  const [isShadesLoading, setShadesLoading] = useState(false);
  const [isSizesLoading, setSizesLoading] = useState(false);
  const [isItemsLoading, setItemsLoading] = useState(false);
  const [selectedDesign, setSelectedDesign] = useState('');
  const [selectedShade, setSelectedShade] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Bundle scan
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const bundleInputRef = useRef(null);

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => {
    if (selectedCategory) {
      fetchDesigns(selectedCategory);
      setSelectedDesign(''); setSelectedShade(''); setSelectedSize(''); setSelectedItem(null);
      setShades([]); setSizes([]); setItems([]);
    }
  }, [selectedCategory]);
  useEffect(() => {
    if (selectedDesign) {
      fetchShades(selectedDesign);
      setSelectedShade(''); setSelectedSize(''); setSelectedItem(null);
      setSizes([]); setItems([]);
    }
  }, [selectedDesign]);
  useEffect(() => {
    if (selectedDesign && selectedShade) {
      fetchSizes(selectedDesign, selectedShade);
      setSelectedSize(''); setSelectedItem(null);
      setItems([]);
    }
  }, [selectedShade]);
  useEffect(() => {
    if (selectedDesign && selectedShade && selectedSize) {
      lookupItems(selectedDesign, selectedShade, selectedSize);
      setSelectedItem(null);
    }
  }, [selectedSize]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const r = await apiClient.get('/goods-transfer/item-master/categories');
      setCategories(r.data.categories || []);
    } catch { toast.error('Failed to load categories'); }
    finally { setCategoriesLoading(false); }
  };
  const fetchDesigns = async (cat) => {
    setDesignsLoading(true);
    try {
      const p = new URLSearchParams(); if (cat) p.append('category4', cat);
      const r = await apiClient.get(`/goods-transfer/item-master/designs?${p}`);
      setDesigns(r.data.designs || []);
    } catch { toast.error('Failed to load designs'); }
    finally { setDesignsLoading(false); }
  };
  const fetchShades = async (design) => {
    setShadesLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('design_name', design);
      if (selectedCategory) p.append('category4', selectedCategory);
      const r = await apiClient.get(`/goods-transfer/item-master/shades?${p}`);
      const s = r.data.shades || [];
      setShades(s);
      if (s.length === 1) { setSelectedShade(s[0]); toast.info(`Auto-selected shade: ${s[0]}`); }
    } catch { /* silent */ }
    finally { setShadesLoading(false); }
  };
  const fetchSizes = async (design, shade) => {
    setSizesLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('design_name', design); p.append('shade', shade);
      if (selectedCategory) p.append('category4', selectedCategory);
      const r = await apiClient.get(`/goods-transfer/item-master/sizes?${p}`);
      const s = r.data.sizes || [];
      setSizes(s);
      if (s.length === 1) { setSelectedSize(s[0]); toast.info(`Auto-selected size: ${s[0]}`); }
    } catch { /* silent */ }
    finally { setSizesLoading(false); }
  };
  const lookupItems = async (design, shade, size) => {
    setItemsLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('design_name', design); p.append('shade', shade); p.append('size', size);
      if (selectedCategory) p.append('category4', selectedCategory);
      const r = await apiClient.get(`/goods-transfer/item-master/lookup?${p}`);
      const it = r.data.items || [];
      setItems(it);
      if (it.length === 1) { setSelectedItem(it[0]); toast.info(`Auto-selected item: ${it[0].barcode}`); }
    } catch { /* silent */ }
    finally { setItemsLoading(false); }
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const code = bundleBarcode.trim();
    if (!code) { toast.error('Please scan bundle barcode'); bundleInputRef.current?.focus(); return; }
    if (scannedBundles.find(b => b.barcode === code)) {
      toast.error('Bundle already scanned');
      setBundleBarcode(''); bundleInputRef.current?.focus(); return;
    }
    setIsProcessing(true);
    try {
      const r = await apiClient.get('/inhouse/design-transfer/validate-bundle', { params: { bundle_code: code } });
      setScannedBundles(prev => [{
        barcode: r.data.bundle_code,
        usn_count: r.data.usn_count,
        current_design_name: r.data.current_design_name,
        current_sku_code: r.data.current_sku_code,
      }, ...prev]);
      toast.success(`Bundle added! Total: ${scannedBundles.length + 1}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid bundle');
    } finally {
      setIsProcessing(false);
      setBundleBarcode('');
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const handleRemoveBundle = (code) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== code));
    toast.success('Bundle removed');
  };

  const handleSubmit = async () => {
    if (!selectedItem) { toast.error('Please select a destination design'); return; }
    if (scannedBundles.length === 0) { toast.error('No bundles scanned'); return; }
    setIsSubmitting(true);
    try {
      const r = await apiClient.post('/inhouse/design-transfer/submit', {
        bundle_codes: scannedBundles.map(b => b.barcode),
        dest_barcode: selectedItem.barcode,
      });
      setResult(r.data);
      toast.success(`Design transferred: ${r.data.bundle_count} bundle(s) · ${r.data.total_usns} USNs`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (Array.isArray(d) ? d.map(e => e.msg || e).join(', ') : 'Submit failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setScannedBundles([]);
    setSelectedCategory(''); setSelectedDesign(''); setSelectedShade(''); setSelectedSize(''); setSelectedItem(null);
    setDesigns([]); setShades([]); setSizes([]); setItems([]);
  };

  const totalUsns = scannedBundles.reduce((s, b) => s + (b.usn_count || 0), 0);
  const isFGSelected = selectedItem !== null;

  const itemOptions = items.map(it => ({ ...it, displayLabel: `${it.description || '-'}${it.pricing_channel ? ` · ${it.pricing_channel}` : ''} (${it.barcode})` }));

  // === Success screen ===
  if (result) {
    return (
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="design-transfer-result">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/inhouse')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-to-inhouse">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Design Transfer · Complete</h1>
              <p className="text-sm text-[#9CA3AF]">Remap successful</p>
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
          <Card className="bg-white border-2 border-emerald-200 shadow-md">
            <CardHeader className="bg-emerald-50">
              <CardTitle className="flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="w-6 h-6" />Design Transfer Complete
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-emerald-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Transfer ID</p>
                  <p className="font-mono text-sm font-semibold" data-testid="result-transfer-id">{result.transfer_id}</p>
                </div>
                <div className="bg-blue-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Bundles</p>
                  <p className="text-2xl font-bold text-blue-700">{result.bundle_count}</p>
                </div>
                <div className="bg-indigo-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Total USNs</p>
                  <p className="text-2xl font-bold text-indigo-700">{result.total_usns}</p>
                </div>
                <div className="bg-amber-50 rounded p-3 text-center">
                  <p className="text-xs text-gray-500">Modified</p>
                  <p className="text-2xl font-bold text-amber-700">{result.modified_count}</p>
                </div>
              </div>
              <div className="border rounded-lg p-3 bg-white">
                <p className="text-xs text-gray-500 mb-1">Destination Design</p>
                <p className="font-semibold text-gray-800">{result.dest_design_name}</p>
                <p className="text-sm text-gray-600 font-mono">{result.dest_barcode}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Shade: <span className="text-gray-700">{result.dest_shade || '—'}</span> · Size: <span className="text-gray-700">{result.dest_size || '—'}</span>
                  {result.dest_description && <> · <span className="text-gray-700">{result.dest_description}</span></>}
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={reset} className="bg-fuchsia-500 hover:bg-fuchsia-600" data-testid="new-transfer-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-1" />New Transfer
                </Button>
                <Button variant="outline" onClick={() => navigate('/sections/inhouse')}>Back to Dashboard</Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="design-transfer-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/inhouse')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="back-to-inhouse">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Design Transfer</h1>
              <p className="text-sm text-[#9CA3AF]">Remap PACKED bundles to a destination design</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Step 1: Operator (logged-in user, auto-filled) */}
        <Card className="bg-white border-2 border-orange-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <User className="w-5 h-5 text-orange-500" />
              Operator
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-lg inline-flex items-center" data-testid="operator-badge">
              <span className="font-mono font-semibold">{operator?.email || operator?.user_id || 'Current User'}</span>
              {operator?.name && <span className="ml-2 text-orange-600">- {operator.name}</span>}
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Select Destination Design (cascade) */}
        <Card className="bg-white border-2 border-green-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
              <Tag className="w-5 h-5 text-green-500" />
              Select Destination Design
            </CardTitle>
            <CardDescription>
              <span className="flex items-center gap-1">
                <Filter className="w-3 h-3" />
                Filter by Category4 first, then select Design → Shade → Size → Item
              </span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <SearchableDropdown
                label="Category 4 (Filter First)"
                placeholder="Select category to filter items..."
                options={categories}
                value={selectedCategory}
                onChange={setSelectedCategory}
                disabled={false}
                isLoading={isCategoriesLoading}
                testId="category4-dropdown"
              />
              {selectedCategory && (
                <p className="mt-2 text-xs text-yellow-700">
                  Filtering by: <strong>{selectedCategory}</strong>
                  <button
                    onClick={() => {
                      setSelectedCategory(''); setSelectedDesign(''); setSelectedShade('');
                      setSelectedSize(''); setSelectedItem(null);
                      setDesigns([]); setShades([]); setSizes([]); setItems([]);
                    }}
                    className="ml-2 text-red-600 hover:underline"
                  >Clear</button>
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <SearchableDropdown label="Design Name"
                placeholder={selectedCategory ? 'Search & select design...' : 'Select category first'}
                options={designs} value={selectedDesign} onChange={setSelectedDesign}
                disabled={!selectedCategory} isLoading={isDesignsLoading}
                testId="design-dropdown" />
              <SearchableDropdown label="Shade"
                placeholder={selectedDesign ? 'Search & select shade...' : 'Select design first'}
                options={shades} value={selectedShade} onChange={setSelectedShade}
                disabled={!selectedDesign} isLoading={isShadesLoading}
                testId="shade-dropdown" />
              <SearchableDropdown label="Size"
                placeholder={selectedShade ? 'Search & select size...' : 'Select shade first'}
                options={sizes} value={selectedSize} onChange={setSelectedSize}
                disabled={!selectedShade} isLoading={isSizesLoading}
                testId="size-dropdown" />
              <SearchableDropdown label="Description / Barcode"
                placeholder={selectedSize ? 'Search & select item...' : 'Select size first'}
                options={itemOptions}
                value={selectedItem?.barcode || ''}
                onChange={(b) => setSelectedItem(items.find(i => i.barcode === b))}
                disabled={!selectedSize || items.length === 0}
                isLoading={isItemsLoading}
                displayKey="displayLabel" valueKey="barcode"
                testId="item-dropdown" />
            </div>

            {selectedItem && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-testid="dest-summary">
                <p className="text-sm font-semibold text-green-800">Destination Design:</p>
                <p className="font-mono text-lg">{selectedItem.barcode}</p>
                <p className="text-sm text-gray-600">
                  {selectedItem.design_name} | {selectedItem.shade} | {selectedItem.size}
                </p>
                <p className="text-sm text-gray-500">{selectedItem.description}</p>
                {selectedItem.pricing_channel && (
                  <p className="text-sm text-gray-600" data-testid="dest-pricing-channel">
                    Pricing Channel: <span className="font-semibold text-green-800">{selectedItem.pricing_channel}</span>
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Scan Bundles */}
        {isFGSelected && (
          <Card className="bg-white border-2 border-blue-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <Package className="w-5 h-5 text-blue-500" />
                Scan PACKED Bundles
              </CardTitle>
              <CardDescription>Scan bundle barcodes from Assortment Packing to remap to the destination design</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBundleScan} className="flex gap-2">
                <Input
                  ref={bundleInputRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  placeholder="Scan Bundle Barcode..."
                  className="text-lg h-12 font-mono"
                  disabled={isProcessing}
                  autoComplete="off"
                  data-testid="bundle-input"
                />
                <Button type="submit" disabled={isProcessing} className="bg-blue-500 hover:bg-blue-600 h-12 px-6" data-testid="add-bundle-btn">
                  {isProcessing ? 'Validating...' : 'Add'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scanned Bundles + Submit */}
        {scannedBundles.length > 0 && (
          <Card className="bg-white border-none shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Scanned Bundles ({scannedBundles.length}) · {totalUsns} USN(s)
                </CardTitle>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !selectedItem}
                  className="bg-fuchsia-500 hover:bg-fuchsia-600"
                  data-testid="submit-design-transfer"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Transferring...' : 'Transfer Design'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scannedBundles.map((b, i) => (
                  <div key={b.barcode} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid={`bundle-row-${b.barcode}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-8">#{scannedBundles.length - i}</span>
                      <div>
                        <span className="font-mono font-semibold">{b.barcode}</span>
                        {b.usn_count ? <span className="ml-2 text-xs text-gray-500">({b.usn_count} USNs)</span> : null}
                        {b.current_design_name && (
                          <div className="text-xs text-gray-500">
                            Current: {b.current_design_name}{b.current_sku_code ? ` · ${b.current_sku_code}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="icon"
                      onClick={() => handleRemoveBundle(b.barcode)}
                      className="text-red-500 hover:bg-red-100"
                      data-testid={`remove-bundle-${b.barcode}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default DesignTransfer;
